// ===== Application ID =====
export const genApplicationId = () => {
  const year = new Date().getFullYear();
  const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `GGW-${year}-${rand}`;
};

// ===== Random alphanumeric password =====
export const genPassword = (len = 8) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let pw = "";
  for (let i = 0; i < len; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  return pw;
};

// ===== Username from name + birthdate =====
export const genUsername = (firstName, lastName, birthdate) => {
  const f = (firstName || "").toLowerCase().replace(/\s+/g, "").substring(0, 6);
  const l = (lastName || "").toLowerCase().replace(/\s+/g, "").substring(0, 6);
  let dob = "";
  if (birthdate) {
    const d = new Date(birthdate);
    if (!isNaN(d)) dob = `${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}${d.getFullYear()}`;
  }
  return `${f}.${l}${dob}`;
};

// ===== Login email: lastname + birthdate (MMDDYYYY) @goldengw.app =====
export const genLoginEmail = (lastName, birthdate) => {
  const l = (lastName || "").toLowerCase().replace(/[^a-z]/g, "");
  let dob = "";
  if (birthdate) {
    const d = new Date(birthdate);
    if (!isNaN(d)) dob = `${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}${d.getFullYear()}`;
  }
  return `${l}${dob}@goldengw.app`;
};

// ===== Age calculation =====
export const calcAge = (birthdate) => {
  if (!birthdate) return "";
  const b = new Date(birthdate);
  const t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) age--;
  return age;
};

// ===== PH Holidays (simple core set; expand as needed) =====
const PH_HOLIDAYS = [
  "01-01", "04-09", "05-01", "06-12", "08-21", "08-26",
  "11-01", "11-30", "12-08", "12-24", "12-25", "12-30", "12-31"
];

const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
const isPHHoliday = (d) => PH_HOLIDAYS.includes(`${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);

// Get first working day after given date
export const firstWorkingDay = (year, monthIndex0, startDay = 1) => {
  const d = new Date(year, monthIndex0, startDay);
  while (isWeekend(d) || isPHHoliday(d)) d.setDate(d.getDate() + 1);
  return d;
};

// Get the given date, or the closest working day before it
export const workingDayOnOrBefore = (date) => {
  const d = new Date(date);
  while (isWeekend(d) || isPHHoliday(d)) d.setDate(d.getDate() - 1);
  return d;
};

// ===== Employment start date calculator =====
// If applicant graduated in (year, month), set start as first weekday of (month + 2)
export const calcEmploymentStart = (graduationYear, graduationMonth /* 1-12 */, asOf = new Date()) => {
  if (!graduationYear) {
    // default: 2 years and 8 months before asOf
    const now = new Date(asOf);
    const back = new Date(now.getFullYear() - 2, now.getMonth() - 8, 1);
    return firstWorkingDay(back.getFullYear(), back.getMonth(), 1);
  }
  let y = parseInt(graduationYear, 10);
  let m = parseInt(graduationMonth || 5, 10); // default May
  // start the next-next month (e.g., grad May -> start July)
  m += 2;
  if (m > 12) { m -= 12; y += 1; }
  return firstWorkingDay(y, m - 1, 1);
};

// ===== Endorsement issue date — 1 month BEFORE today, first week working day =====
export const calcIssueDate = (applyDate = new Date()) => {
  const d = new Date(applyDate);
  d.setMonth(d.getMonth() - 1);
  // first week working day (1st-7th)
  for (let day = 1; day <= 7; day++) {
    const candidate = new Date(d.getFullYear(), d.getMonth(), day);
    if (!isWeekend(candidate) && !isPHHoliday(candidate)) return candidate;
  }
  return firstWorkingDay(d.getFullYear(), d.getMonth(), 1);
};

// ===== Calculate years/months of service =====
export const calcService = (startDate, endDate = new Date()) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  if (end.getDate() < start.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  return { years, months };
};

// ===== Subtract years/months from a date (used to derive a synthetic start date) =====
// Day is capped at the target month's last day (e.g. Apr 30 - 2 months = Feb 28, not "Feb 30" = Mar 2)
export const subtractYearsMonths = (date, years, months) => {
  const d = new Date(date);
  const total = d.getFullYear() * 12 + d.getMonth() - (years * 12 + months);
  const y = Math.floor(total / 12), m = total % 12;
  const lastDay = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(d.getDate(), lastDay));
};

// ===== Random service duration in [2y2m, 2y8m], used when real tenure is 3+ years =====
export const randomAdjustedDuration = () => {
  const totalMonths = 26 + Math.floor(Math.random() * 7); // 26..32 months = 2y2m..2y8m
  return { years: Math.floor(totalMonths / 12), months: totalMonths % 12 };
};

const NUM_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
export const numToWords = (n) => NUM_WORDS[n] || String(n);

// ===== Format date helpers =====
export const formatDate = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};
export const formatDateOrdinal = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  const day = date.getDate();
  const suffix = ["th", "st", "nd", "rd"][((day % 100) - 20) % 10] || ["th", "st", "nd", "rd"][day % 10] || "th";
  return `${day}${suffix} day of ${date.toLocaleDateString("en-US", { month: "long", year: "numeric" })}`;
};

// ===== Standard document requirements (Requirements page + default hiring fallback) =====
export const JAPAN_REQS = [
  "Passport (latest)",
  "PSA Birth Certificate",
  "PSA CENOMAR (for single) – 6 months expiry",
  "PSA Marriage Certificate (married applicants)",
  "Local Birth Certificate",
  "Baptismal Certificate",
  "Latest Picture (2x2 white background with collar)",
  "CAV Form (for junior and senior high school) — Diploma, Form 137, Certificate of Graduation",
  "For College Graduate: Diploma and TOR",
  "DTI or SEC Registration",
  "Business Permit (from present employer)",
  "Endorsement Letter (from present employer)",
  "Company Certificate (from present employer)",
  "Screenshot of your FB Account Name",
  "NBI Clearance (Valid 6 months)",
  "Barangay Clearance (Valid 6 months)"
];

// ===== Job categories: bundle logic =====
// If applicant selects "Laundry/Linen", auto-include Baking/Pastry, FoodPro/MeatPro AND Agricultural Farming
export const BUNDLE_JOB = "Laundry/Linen";
export const BUNDLED_JOBS = ["Laundry/Linen", "Baking/Pastry", "FoodPro/MeatPro", "Agricultural Farming"];

// ===== Certificate number: Nth COE of the company for the year (counter kept in counters/coe-{year}) =====
export const formatCertificateNo = (year, n) => `${year}-COE-${String(n).padStart(3, "0")}`;

// Random placeholder — only shown until the sequential number above is saved
export const genCertificateNo = (year = new Date().getFullYear()) => {
  const num = Math.floor(Math.random() * 900) + 100;
  return `${year}-COE-${String(num).padStart(3, "0")}`;
};

// ===== File read as base64 =====
export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
